import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { Icon } from './Icon.js';
import { cx } from './utils.js';

const WEEKDAYS = [
  ['Δε', 'Mo'],
  ['Τρ', 'Tu'],
  ['Τε', 'We'],
  ['Πε', 'Th'],
  ['Πα', 'Fr'],
  ['Σα', 'Sa'],
  ['Κυ', 'Su'],
] as const;
const MONTHS = [
  ['Ιανουάριος', 'January'],
  ['Φεβρουάριος', 'February'],
  ['Μάρτιος', 'March'],
  ['Απρίλιος', 'April'],
  ['Μάιος', 'May'],
  ['Ιούνιος', 'June'],
  ['Ιούλιος', 'July'],
  ['Αύγουστος', 'August'],
  ['Σεπτέμβριος', 'September'],
  ['Οκτώβριος', 'October'],
  ['Νοέμβριος', 'November'],
  ['Δεκέμβριος', 'December'],
] as const;

export type DateInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  className?: string;
  id?: string;
  clearable?: boolean;
  'aria-label'?: string;
};

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function toDateOnly(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
    return '';
  return `${year}-${pad(month)}-${pad(day)}`;
}

function ymd(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function display(value: string): string {
  const norm = toDateOnly(value);
  if (!norm) return '';
  const [year, month, day] = norm.split('-');
  return `${day}/${month}/${year}`;
}

/** Popup position: below the field, or above when the calendar does not fit. */
function placeCalendar(
  anchor: DOMRect,
  menuHeight: number,
): { top: number; left: number; width: number } {
  const margin = 8;
  const gap = 6;
  const width = Math.max(anchor.width, 280);
  const left = Math.max(margin, Math.min(anchor.left, window.innerWidth - width - margin));
  const height = menuHeight > 0 ? menuHeight : 340;
  const spaceBelow = window.innerHeight - anchor.bottom - gap;
  const spaceAbove = anchor.top - gap;
  const placeAbove = height + margin > spaceBelow && spaceAbove > spaceBelow;
  const top = placeAbove ? Math.max(margin, anchor.top - gap - height) : anchor.bottom + gap;
  return { top, left, width };
}

function mondayGrid(year: number, month: number): Array<Date | null> {
  const first = new Date(year, month, 1).getDay();
  const offset = first === 0 ? 6 : first - 1;
  const days = new Date(year, month + 1, 0).getDate();
  const grid: Array<Date | null> = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= days; day += 1) grid.push(new Date(year, month, day));
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

export function DateInput({
  value,
  onChange,
  placeholder,
  disabled = false,
  min,
  max,
  className,
  id,
  clearable = true,
  'aria-label': ariaLabel,
}: DateInputProps): ReactNode {
  const { text } = useAdminLocale();
  const resolvedPlaceholder = placeholder ?? text('ΗΗ/ΜΜ/ΕΕΕΕ', 'DD/MM/YYYY');
  const autoId = useId();
  const triggerId = id ?? autoId;
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selected = toDateOnly(value);
  const selectedDate = selected ? new Date(`${selected}T12:00:00`) : null;
  const [view, setView] = useState(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!selected) return;
    const date = new Date(`${selected}T12:00:00`);
    setView(new Date(date.getFullYear(), date.getMonth(), 1));
  }, [selected]);

  const syncPosition = (): void => {
    const box = rootRef.current?.getBoundingClientRect();
    if (!box) return;
    const next = placeCalendar(box, menuRef.current?.offsetHeight ?? 0);
    setRect((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.width === next.width
        ? prev
        : next,
    );
  };

  useLayoutEffect(() => {
    if (!open || !rootRef.current) {
      setRect(null);
      return;
    }
    syncPosition();
    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !rect || !menuRef.current) return;
    syncPosition();
    const menu = menuRef.current;
    const observer = new ResizeObserver(() => syncPosition());
    observer.observe(menu);
    return () => observer.disconnect();
  }, [open, rect !== null]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function blocked(date: Date): boolean {
    const key = ymd(date);
    if (min && key < toDateOnly(min)) return true;
    if (max && key > toDateOnly(max)) return true;
    return false;
  }

  function pick(date: Date): void {
    if (blocked(date)) return;
    onChange(ymd(date));
    setOpen(false);
  }

  const grid = mondayGrid(view.getFullYear(), view.getMonth());
  const today = ymd(new Date());
  const menu =
    open && rect
      ? createPortal(
          <div
            ref={menuRef}
            role="dialog"
            aria-label={text('Ημερολόγιο', 'Calendar')}
            className="fixed z-[80] rounded-xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900"
            style={{ top: rect.top, left: rect.left, width: rect.width }}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                className="grid size-8 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                aria-label={text('Προηγούμενος μήνας', 'Previous month')}
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              >
                <Icon name="chevron-left" className="size-4" />
              </button>
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {MONTHS[view.getMonth()]
                  ? text(MONTHS[view.getMonth()]![0], MONTHS[view.getMonth()]![1])
                  : ''}{' '}
                {view.getFullYear()}
              </span>
              <button
                type="button"
                className="grid size-8 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                aria-label={text('Επόμενος μήνας', 'Next month')}
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              >
                <Icon name="chevron-right" className="size-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500">
              {WEEKDAYS.map((day) => (
                <span key={day[1]} className="py-1">
                  {text(day[0], day[1])}
                </span>
              ))}
              {grid.map((cell, index) => {
                const key = cell ? ymd(cell) : `empty-${index}`;
                const isSelected = cell ? key === selected : false;
                const isToday = cell ? key === today : false;
                const isBlocked = cell ? blocked(cell) : true;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!cell || isBlocked}
                    onClick={() => cell && pick(cell)}
                    className={cx(
                      'grid h-8 place-items-center rounded-lg text-sm',
                      !cell && 'invisible',
                      isBlocked && cell && 'cursor-not-allowed text-slate-300 dark:text-slate-600',
                      isSelected &&
                        'bg-[color:var(--admin-selection-bg,#2563EB)] font-medium text-[color:var(--admin-selection-text,#FFFFFF)]',
                      !isSelected &&
                        !isBlocked &&
                        'text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800',
                      isToday &&
                        !isSelected &&
                        'ring-1 ring-[color:var(--admin-selection-bg,#2563EB)]',
                    )}
                  >
                    {cell ? cell.getDate() : ''}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 dark:border-slate-800">
              <button
                type="button"
                className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                onClick={() => {
                  const now = new Date();
                  if (!blocked(now)) pick(now);
                }}
              >
                {text('Σήμερα', 'Today')}
              </button>
              {clearable ? (
                <button
                  type="button"
                  className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  {text('Καθαρισμός', 'Clear')}
                </button>
              ) : null}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cx('relative min-w-0', className)} data-ui="date-input">
      <button
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cx(
          'flex h-10 w-full items-center gap-2 rounded-lg border bg-white px-3 text-left text-sm shadow-sm',
          'border-slate-300 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-950 dark:hover:border-slate-500',
          'focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <Icon name="calendar" className="size-4 shrink-0 text-slate-400" />
        <span
          className={cx(
            'min-w-0 flex-1 truncate',
            selected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400',
          )}
        >
          {selected ? display(selected) : resolvedPlaceholder}
        </span>
        {clearable && selected && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={text('Καθαρισμός ημερομηνίας', 'Clear date')}
            className="grid size-6 place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            onClick={(event) => {
              event.stopPropagation();
              onChange('');
            }}
          >
            <Icon name="x" className="size-3.5" />
          </span>
        ) : (
          <Icon name="chevron" className="size-4 shrink-0 text-slate-400" />
        )}
      </button>
      {menu}
    </div>
  );
}
